import { Trophy } from 'lucide-react';
import React from 'react';
import type { GameData, Player, Team } from '@/types/game';

export const teamColors = {
  Yellow: { bg: 'bg-team-yellow', text: 'text-team-yellow', border: 'border-team-yellow' },
  Red: { bg: 'bg-team-red', text: 'text-team-red', border: 'border-team-red' },
  Blue: { bg: 'bg-team-blue', text: 'text-team-blue', border: 'border-team-blue' },
  Ocean: { bg: 'bg-team-blue', text: 'text-team-blue', border: 'border-team-blue' },
  Green: { bg: 'bg-team-green', text: 'text-team-green', border: 'border-team-green' },
  Purple: { bg: 'bg-team-purple', text: 'text-team-purple', border: 'border-team-purple' },
} as const;

export const defaultTeamColors = { bg: 'bg-muted', text: 'text-foreground', border: 'border-border' };

// Single definition lives in the shared module (used by website + edge functions).
export { normalizeTeamName, hasTeamName } from '@shared/player-stats';
import { normalizeTeamName } from '@shared/player-stats';



/**
 * Link-Sanitizer für externe Scorekarten (z. B. my.lasermaxx.com).
 * Aktuell nicht in der UI verwendet, aber bewusst erhalten, damit die Links
 * später ohne Rekonstruktion wieder aktiviert werden können.
 */
export const sanitizePlayerUrl = (value: unknown): string => {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  try {
    const parsed = new URL(raw);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return '';
    return parsed.toString();
  } catch {
    return '';
  }
};


export const getTeamColors = (teamName: string) => {
  const normalizedTeamName = normalizeTeamName(teamName);
  return teamColors[normalizedTeamName as keyof typeof teamColors] || defaultTeamColors;
};

export const formatGameNumber = (gameNumber: string | number) => {
  return String(gameNumber).padStart(3, '0');
};

/**
 * Parse German date format "DD.MM.YYYY - HH:MM Uhr" from game_data.timestamp
 */
export const parseGameDate = (gameData: any): Date => {
  try {
    const timestampStr = gameData?.timestamp;
    if (timestampStr && typeof timestampStr === 'string') {
      const match = timestampStr.match(/(\d{2})\.(\d{2})\.(\d{4})\s*-\s*(\d{2}):(\d{2})/);
      if (match) {
        const [, day, month, year, hour, minute] = match;
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(minute));
      }
    }
  } catch {
    // Fallback
  }
  return new Date(0);
};

const toDisplayNumber = (value: unknown) => String(value ?? '0');

export const normalizeGameData = (data: any): GameData => {
  const game = String(data?.game ?? '');

  const players: Player[] = (Array.isArray(data?.players) ? data.players : []).map((player: any) => {
    const shots = Number(player?.shots ?? 0);
    const hits = Number(player?.hits ?? 0);
    const accuracy = player?.accurancy ?? player?.accuracy ?? (shots > 0 ? Math.round((hits / shots) * 100) : 0);

    return {
      weste: String(player?.weste ?? ''),
      name: String(player?.name ?? ''),
      team: normalizeTeamName(player?.team),
      score: toDisplayNumber(player?.score),
      shots: toDisplayNumber(player?.shots),
      accurancy: toDisplayNumber(accuracy),
      playerurl: sanitizePlayerUrl(player?.playerurl),
    };
  });

  const teams: Team[] = (Array.isArray(data?.teams) ? data.teams : [])
    .map((team: any) => ({
      team: normalizeTeamName(team?.team ?? team?.name ?? team?.id),
      score: toDisplayNumber(team?.score),
      rank: toDisplayNumber(team?.rank),
    }))
    .sort((a, b) => parseInt(b.score, 10) - parseInt(a.score, 10))
    .map((team, index) => ({ ...team, rank: team.rank && team.rank !== '0' ? team.rank : String(index + 1) }));

  return {
    ...data,
    game,
    timestamp: String(data?.timestamp ?? ''),
    file: data?.file ?? (game ? `${game.padStart(4, '0')}.game` : undefined),
    teams,
    players,
  };
};

export const formatTimestamp = (timestamp: string) => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'gerade eben';
  if (diffMins < 60) return `vor ${diffMins} Min.`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `vor ${diffHours} Std.`;

  return date.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const isRateLimitError = (str: string) =>
  str.includes('Rate limit') ||
  str.includes('429') ||
  str.includes('rate limit') ||
  str.includes('Rate limit exceeded');
